/**
 * The automation builder — Step 89, Kai's stated preference over the
 * three-block form ("that can work if WE MUST, but I prefer the other visual
 * layout").
 *
 * ── Why this is a VERTICAL FLOW, not a free canvas ─────────────────────
 *
 * The reference draws a free-positioned node canvas. We deliberately do not,
 * and the reason is in its own documentation: *"workflows do not support
 * loop-back or rejoining flows. Separate condition flows cannot rejoin the
 * main flow."* Their graph is a TREE that happens to be drawn on a plane —
 * every node has one parent, and the (x, y) coordinates carry no meaning
 * beyond aesthetics.
 *
 * So a free canvas costs drag-and-drop physics, viewport maths, edge routing
 * and a stored layout that can disagree with the graph — to display a
 * structure that is already a list with branches. A vertical flow renders the
 * same information, reads top-to-bottom like the sentence the Summary panel
 * shows, and cannot drift from the data because there is no layout to store.
 *
 * The engine's `nodes`/`edges` shape is unchanged and fully general: if a
 * genuine DAG is ever needed, the storage already supports it and only this
 * renderer changes.
 *
 * ── The safety behaviours, copied deliberately ─────────────────────────
 *
 *  - **Summary panel**: the rule rendered back as an English sentence, live.
 *    A rules engine you cannot read at a glance is one nobody trusts, and
 *    this is the single cheapest defence against a rule that does something
 *    other than what its author meant.
 *  - **Validate before activating**, showing EVERY problem at once.
 *  - Editing an active rule moves it to draft (enforced server-side).
 */
import { useMemo } from 'react';
import { ArrowDown, GitBranch, Plus, Repeat, Timer as TimerIcon, Trash2, Workflow, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { CONDITION_OPS, ConditionBuilder, type ConditionField } from '../../components/ConditionBuilder';
import type { Condition } from '@itsm/shared';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select } from '../../components/ui/select';
import { cn } from '../../lib/utils';

export interface GraphNode {
  id: string;
  type: 'trigger' | 'condition' | 'action' | 'timer' | 'loop' | 'subflow';
  config: Record<string, unknown>;
}
export interface GraphEdge {
  from: string;
  to: string;
  branch?: 'yes' | 'no' | 'each' | 'done';
}
export interface Graph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export const TRIGGERS: { key: string; label: string }[] = [
  { key: 'ticket.created', label: 'a ticket is created' },
  { key: 'ticket.updated', label: 'a ticket is updated' },
  { key: 'ticket.status_changed', label: 'the status changes' },
  { key: 'ticket.priority_changed', label: 'the priority changes' },
  { key: 'ticket.assigned', label: 'a ticket is assigned' },
  { key: 'requester.replied', label: 'the requester replies' },
  { key: 'agent.replied', label: 'an agent replies' },
  { key: 'ticket.note_added', label: 'a private note is added' },
  { key: 'catalogue.request_submitted', label: 'a service request is submitted' },
  { key: 'schedule.hourly', label: 'every hour (scheduled)' },
  // A subflow is never fired by an event — it runs only where another rule
  // calls it. Offered here because choosing this trigger is how you MAKE one.
  { key: 'subflow.called', label: 'another rule calls it (subflow)' },
];

/** The fields a condition may match on — exactly `TicketFacts` in the engine.
 *  A field offered here that the engine cannot see would produce a rule that
 *  silently never matches. */
/** Option lists carry a LABEL, never the raw key. `service_request` in a
 *  dropdown is a database value that escaped into the product (Kai: "Not
 *  friendly text again"). The stored value is unchanged — only what a person
 *  reads. */
type Opt = { value: string; label: string };
const opt = (v: string, l: string): Opt => ({ value: v, label: l });
const LEVELS: Opt[] = [opt('low', 'Low'), opt('medium', 'Medium'), opt('high', 'High')];
const PRIORITIES: Opt[] = [...LEVELS, opt('urgent', 'Urgent')];
const TYPES: Opt[] = [opt('incident', 'Incident'), opt('service_request', 'Service request')];
const CATEGORIES: Opt[] = [
  opt('open', 'Open'),
  opt('pending', 'Pending'),
  opt('resolved', 'Resolved'),
  opt('closed', 'Closed'),
];

const FIELDS: ConditionField[] = [
  { key: 'type', label: 'Type', options: TYPES },
  { key: 'status', label: 'Status' },
  { key: 'statusCategory', label: 'Status category', options: CATEGORIES },
  { key: 'priority', label: 'Priority', options: PRIORITIES },
  { key: 'urgency', label: 'Urgency', options: LEVELS },
  { key: 'impact', label: 'Impact', options: LEVELS },
  { key: 'groupId', label: 'Group' },
  { key: 'assigneeId', label: 'Assignee' },
  { key: 'source', label: 'Source' },
  { key: 'catalogueItemId', label: 'Catalogue item' },
  // Step 158/159 — lets a rule say "this is part of something bigger", which
  // both directions of the relationship family need.
  { key: 'parentId', label: 'Parent ticket' },
  // Step 161 — the desk's own department (Step 104 gave requesters one).
  { key: 'departmentId', label: 'Department' },
  // Kai's real rules match on the SUBJECT constantly — "Create new SR from
  // email based on Subject", "New Security photo update via email". The
  // engine has always had it in `TicketFacts`; it was simply never offered.
  { key: 'subject', label: 'Subject' },
  // The requester's directory attributes. Two of his live rules route by
  // them ("Spanish and Dutch location user tickets assigned to Local group",
  // "NL location user tickets to Outdoor NL group") and neither could be
  // expressed at all before.
  { key: 'requesterDepartment', label: 'Requester department' },
  { key: 'requesterLocation', label: 'Requester location' },
  { key: 'requesterJobTitle', label: 'Requester job title' },
  { key: 'requesterCountry', label: 'Requester country' },
  // Step 206 — the two things anybody reaches for first and neither existed.
  // A rule could see the requester's DEPARTMENT but not their address, and
  // could match the subject but not a word of the message under it.
  { key: 'requesterEmail', label: 'Requester email address' },
  // Separate on purpose: the description never changes and is what a
  // "ticket raised" rule means by the body; the latest message is what a
  // "requester replied" rule means. One field could not serve both.
  { key: 'descriptionText', label: 'Description text (the first message)' },
  { key: 'latestMessageText', label: 'Latest message text' },
  // Step 120 — the time facts, in hours. What a scheduled sweep reads:
  // "nobody has replied in three days", "sat in Waiting for a week".
  { key: 'hoursSinceUpdated', label: 'Hours since last update', numeric: true },
  { key: 'hoursSinceCreated', label: 'Hours since raised', numeric: true },
  { key: 'hoursInStatus', label: 'Hours in current status', numeric: true },
  { key: 'hoursSinceRequesterReply', label: 'Hours since the requester last wrote', numeric: true },
  { key: 'hoursSinceAgentReply', label: 'Hours since an agent last replied', numeric: true },
  // Step 143 — a change planned inside a freeze window.
  {
    key: 'inFreezeWindow',
    label: 'Planned inside a freeze window',
    options: [
      { value: 'yes', label: 'yes' },
      { value: 'no', label: 'no' },
    ],
    omitOps: ['contains', 'notContains', 'isEmpty', 'isNotEmpty', 'in', 'notIn'],
  },
  {
    key: 'lastReplyBy',
    label: 'Who spoke last',
    options: [
      { value: 'requester', label: 'the requester' },
      { value: 'agent', label: 'an agent' },
      { value: 'none', label: 'nobody yet' },
    ],
    omitOps: ['contains', 'notContains', 'isEmpty', 'isNotEmpty'],
  },
];

const ACTIONS: { key: string; label: string; options?: Opt[]; free?: boolean; novalue?: boolean }[] = [
  { key: 'set_status', label: 'Set status to' },
  { key: 'set_priority', label: 'Set priority to', options: PRIORITIES },
  { key: 'set_urgency', label: 'Set urgency to', options: LEVELS },
  { key: 'set_impact', label: 'Set impact to', options: LEVELS },
  { key: 'set_type', label: 'Set type to', options: TYPES },
  { key: 'assign_group', label: 'Assign to group' },
  { key: 'assign_agent', label: 'Assign to agent' },
  // "Ignore Automatic reply emails" — a rule whose whole job is to stop.
  {
    // Step 158 — the people half of the relationship family.
    key: 'watch_parent_assignee',
    label: "Add the parent's agent as a watcher",
    novalue: true,
  },
  {
    // Step 159 — the UPWARD one. Worded as "when they are all done" because
    // the single most likely misreading is that it fires on this one.
    key: 'close_parent_when_children_done',
    label: 'When every child is done, set the parent to',
    options: [
      { value: 'resolved', label: 'Resolved' },
      { value: 'closed', label: 'Closed' },
    ],
  },
  {
    // Step 157 — the only action that reaches past this ticket. Worded as
    // "its child and linked tickets" rather than "downstream", because the
    // person picking it needs to know exactly what it will touch.
    key: 'cascade_status',
    label: 'Also set its child and linked tickets to',
    options: [
      { value: 'resolved', label: 'Resolved' },
      { value: 'closed', label: 'Closed' },
    ],
  },
  { key: 'stop_processing', label: 'Stop processing this ticket', novalue: true },
  { key: 'add_note', label: 'Add a private note', free: true },
  { key: 'web_request', label: 'Send a web request', free: true },
  // Step 161 — the nine the "everything is a rule" shape was missing.
  { key: 'clear_assignee', label: 'Clear the assignee', novalue: true },
  { key: 'clear_group', label: 'Clear the group', novalue: true },
  { key: 'set_department', label: 'Set department to' },
  { key: 'add_watcher', label: 'Add a watcher' },
  { key: 'add_tag', label: 'Add the tag' },
  { key: 'send_email', label: 'Send an email', free: true },
  { key: 'create_child_ticket', label: 'Raise a child request for' },
  { key: 'associate_to', label: 'Associate to problem/change #' },
  {
    key: 'cascade_priority',
    label: 'Also set its child and linked tickets to priority',
    options: PRIORITIES,
  },
  // Step 145 — assignment policy as an action. Empty value = the group's own setting.
  {
    key: 'auto_assign',
    label: 'Auto-assign within its group',
    options: [
      { value: '', label: "the group's setting" },
      { value: 'round_robin', label: 'round-robin' },
      { value: 'least_load', label: 'least loaded agent' },
    ],
  },
  // Step 101 — send it to a CAB board.
  { key: 'send_for_approval', label: 'Send for approval to' },
  { key: 'mark_as_spam', label: 'Mark as spam', novalue: true },
  { key: 'move_to_trash', label: 'Move to the bin', novalue: true },
];

const uid = () => Math.random().toString(36).slice(2, 9);

/** Sentinel for "the agent who triggered this rule" inside the Assign-to-agent
 *  picker. Chosen as a value no UUID can collide with. */
const ACTOR_VALUE = '@actor';

/**
 * The editor works on a NESTED step list, not the flat chain it used to.
 *
 * A loop block contains its body, and a body is just another chain, so the
 * only honest editing model is a tree. The graph on the wire is unchanged —
 * `parseSteps`/`toGraph` are the two halves of one bijection, and edges are
 * still DERIVED from the order rather than hand-maintained, which is what
 * makes an orphaned edge structurally impossible in this editor.
 */
export type Step =
  | { id: string; type: 'condition' | 'action' | 'timer' | 'subflow'; config: Record<string, unknown> }
  | { id: string; type: 'loop'; config: Record<string, unknown>; body: Step[] };

interface Walk {
  byId: Map<string, GraphNode>;
  out: Map<string, GraphEdge[]>;
}

function indexGraph(graph: Graph): Walk {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const out = new Map<string, GraphEdge[]>();
  for (const e of graph.edges) out.set(e.from, [...(out.get(e.from) ?? []), e]);
  return { byId, out };
}

function parseChain(w: Walk, startId: string | undefined, seen = new Set<string>()): Step[] {
  const list: Step[] = [];
  let cursor = startId;
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const n = w.byId.get(cursor);
    if (!n) break;
    const outs = w.out.get(n.id) ?? [];
    if (n.type === 'loop') {
      const body = outs.find((e) => e.branch === 'each')?.to;
      list.push({ id: n.id, type: 'loop', config: n.config, body: parseChain(w, body, seen) });
      cursor = (outs.find((e) => e.branch === 'done') ?? outs.find((e) => !e.branch))?.to;
    } else if (n.type === 'condition') {
      list.push({ id: n.id, type: 'condition', config: n.config });
      cursor = outs.find((e) => e.branch === 'yes')?.to;
    } else if (n.type === 'trigger') {
      cursor = outs[0]?.to;
    } else {
      list.push({ id: n.id, type: n.type, config: n.config });
      cursor = outs[0]?.to;
    }
  }
  return list;
}

export function parseSteps(graph: Graph): Step[] {
  const w = indexGraph(graph);
  return parseChain(w, w.out.get('t')?.[0]?.to);
}

/** Step 120 — the trigger node's own settings (a scheduled rule's cooldown)
 *  ride through a rebuild; before this `toGraph` wrote `config: {}` and every
 *  edit to a step silently reset them. */
export function triggerConfigOf(graph: Graph): Record<string, unknown> {
  return graph.nodes.find((n) => n.type === 'trigger')?.config ?? {};
}

export function toGraph(steps: Step[], triggerConfig: Record<string, unknown> = {}): Graph {
  const nodes: GraphNode[] = [{ id: 't', type: 'trigger', config: triggerConfig }];
  const edges: GraphEdge[] = [];
  const chain = (list: Step[], from: string, branch?: GraphEdge['branch']): void => {
    let prev = from;
    let prevBranch = branch;
    for (const s of list) {
      nodes.push({ id: s.id, type: s.type, config: s.config });
      edges.push(prevBranch ? { from: prev, to: s.id, branch: prevBranch } : { from: prev, to: s.id });
      if (s.type === 'loop') {
        // The body's LAST node gets no outgoing edge on purpose: the engine
        // returns to the loop node when a body runs off its end, so there is
        // no back-edge to store — which keeps the saved graph acyclic.
        chain(s.body, s.id, 'each');
        prevBranch = 'done';
      } else if (s.type === 'condition') {
        prevBranch = 'yes';
      } else {
        prevBranch = undefined;
      }
      prev = s.id;
    }
  };
  chain(steps, 't');
  return { nodes, edges };
}

/** The rule as an English sentence — the Summary panel's whole job. */
export function describeGraph(graph: Graph, trigger: string): string {
  const t = TRIGGERS.find((x) => x.key === trigger)?.label ?? trigger;
  const parts: string[] = [`When ${t}`];

  const say = (steps: Step[], indent: string): void => {
    for (const s of steps) {
      if (s.type === 'condition') {
        const c = s.config.condition as {
          op: string;
          fieldKey?: string;
          value?: unknown;
          conditions?: unknown[];
        };
        parts.push(`${indent}and ${describeCondition(c)}`);
      } else if (s.type === 'timer') {
        parts.push(`${indent}→ wait ${describeMinutes(Number(s.config.minutes) || 0)}`);
      } else if (s.type === 'loop') {
        const src = String(s.config.collection ?? '');
        const label =
          src === 'field_values'
            ? `each value of ${String(s.config.fieldKey ?? '?')}`
            : `each of the ${src.replace(/_/g, ' ')}`;
        parts.push(`${indent}→ for ${label}:`);
        say(s.body, `${indent}   `);
      } else if (s.type === 'subflow') {
        parts.push(`${indent}→ run the subflow "${String(s.config.ruleName ?? s.config.ruleId ?? '?')}"`);
      } else {
        const a = ACTIONS.find((x) => x.key === s.config.action);
        parts.push(
          `${indent}→ ${a?.label ?? String(s.config.action)} ${String(s.config.value ?? '')}`.trimEnd(),
        );
      }
    }
  };
  say(parseSteps(graph), '');
  return parts.join(' ');
}

function describeCondition(c: {
  op: string;
  fieldKey?: string;
  value?: unknown;
  conditions?: unknown[];
}): string {
  if (c?.op === 'and' || c?.op === 'or') {
    const inner = ((c.conditions ?? []) as { op: string; fieldKey?: string; value?: unknown }[]).map((x) =>
      describeCondition(x),
    );
    return inner.join(c.op === 'or' ? ' or ' : ' and ');
  }
  const f = FIELDS.find((x) => x.key === c?.fieldKey)?.label ?? c?.fieldKey ?? '?';
  const opLabel = CONDITION_OPS.find((o) => o.op === c?.op)?.label ?? c?.op ?? 'is';
  const v = Array.isArray(c?.value) ? c.value.join(' or ') : (c?.value ?? '');
  return `${f} ${opLabel}${v === '' ? '' : ` ${String(v)}`}`;
}

/** Minutes as something a person would say. A rule that reads "wait 2880" is
 *  a rule nobody checks. */
export function describeMinutes(mins: number): string {
  if (mins <= 0) return 'no time at all';
  if (mins % 1440 === 0) return `${mins / 1440} day${mins === 1440 ? '' : 's'}`;
  if (mins % 60 === 0) return `${mins / 60} hour${mins === 60 ? '' : 's'}`;
  return `${mins} minute${mins === 1 ? '' : 's'}`;
}

/** A blank rule: trigger → one action. The smallest thing that validates. */
export function emptyGraph(): Graph {
  const a = uid();
  return {
    nodes: [
      { id: 't', type: 'trigger', config: {} },
      { id: a, type: 'action', config: { action: 'set_priority', value: 'high' } },
    ],
    edges: [{ from: 't', to: a }],
  };
}

export interface BuilderMeta {
  groups: { id: string; name: string }[];
  agents: { id: string; name: string | null; email: string }[];
  statuses: { key: string; name: string }[];
  /** Live rules triggered by "another rule calls it". Only live ones are
   *  offered — see the route. */
  subflows: { id: string; name: string }[];
  collections: { key: string; label: string; needsFieldKey: boolean }[];
  items: { id: string; name: string }[];
  /** Step 161 */
  departments: { id: string; name: string }[];
  /** Step 101 — CAB boards, for `send_for_approval`. */
  boards: { id: string; name: string }[];
}

export function RuleBuilder({
  graph,
  trigger,
  onChange,
  groups,
  agents,
  statuses,
  subflows,
  collections,
  items,
  departments,
  boards,
}: {
  graph: Graph;
  trigger: string;
  onChange: (g: Graph) => void;
} & BuilderMeta) {
  const steps = useMemo(() => parseSteps(graph), [graph]);

  const valueOptions = (key: string): { value: string; label: string }[] => {
    if (key === 'groupId' || key === 'assign_group')
      return groups.map((g) => ({ value: g.id, label: g.name }));
    if (key === 'assigneeId') return agents.map((a) => ({ value: a.id, label: a.name ?? a.email }));
    if (key === 'assign_agent')
      // The triggering agent sits INSIDE the agent picker rather than being a
      // separate action, because "assign it to whoever replied" is a choice of
      // WHO, not a different kind of assignment (Kai: "needs to be assign to
      // agent then an option of triggerer"). It still stores its own action
      // key, so the engine is unchanged.
      return [
        { value: ACTOR_VALUE, label: 'The agent who triggered this rule' },
        ...agents.map((a) => ({ value: a.id, label: a.name ?? a.email })),
      ];
    if (key === 'status' || key === 'set_status')
      return statuses.map((s) => ({ value: s.key, label: s.name }));
    if (key === 'catalogueItemId' || key === 'create_child_ticket')
      return items.map((i) => ({ value: i.id, label: i.name }));
    // Step 161
    if (key === 'add_watcher') return agents.map((a) => ({ value: a.id, label: a.name ?? a.email }));
    if (key === 'departmentId' || key === 'set_department')
      return departments.map((d) => ({ value: d.id, label: d.name }));
    if (key === 'send_for_approval') return boards.map((b) => ({ value: b.id, label: b.name }));
    return [];
  };

  const triggerConfig = triggerConfigOf(graph);
  const write = (next: Step[]) => onChange(toGraph(next, triggerConfig));
  const writeTrigger = (cfg: Record<string, unknown>) => onChange(toGraph(steps, cfg));

  /** Edit one step ANYWHERE in the tree, by id. A loop body is just another
   *  list, so every mutation is the same recursive map. */
  const mapSteps = (list: Step[], fn: (s: Step) => Step | null): Step[] =>
    list
      .map((s) => {
        const next = fn(s);
        if (next === null) return null;
        return next.type === 'loop' ? { ...next, body: mapSteps(next.body, fn) } : next;
      })
      .filter((s): s is Step => s !== null);

  const update = (id: string, config: Record<string, unknown>) =>
    write(mapSteps(steps, (s) => (s.id === id ? { ...s, config } : s)));

  const remove = (id: string) => write(mapSteps(steps, (s) => (s.id === id ? null : s)));

  /** Append into a list identified by its owning loop id (`null` = top level). */
  const append = (parentId: string | null, step: Step) => {
    if (parentId === null) return write([...steps, step]);
    write(
      mapSteps(steps, (s) =>
        s.id === parentId && s.type === 'loop' ? { ...s, body: [...s.body, step] } : s,
      ),
    );
  };

  const makeStep = (type: Step['type']): Step => {
    const id = uid();
    switch (type) {
      case 'condition':
        return { id, type, config: { condition: { op: 'eq', fieldKey: 'priority', value: 'urgent' } } };
      case 'timer':
        return { id, type, config: { minutes: 240 } };
      case 'loop':
        return { id, type, config: { collection: collections[0]?.key ?? 'linked_tickets' }, body: [] };
      case 'subflow':
        return { id, type, config: { ruleId: subflows[0]?.id ?? '', ruleName: subflows[0]?.name ?? '' } };
      default:
        return { id, type: 'action', config: { action: 'set_priority', value: 'high' } };
    }
  };

  return (
    <div className="space-y-1">
      {/* Trigger — the fixed head of every rule. */}
      <div className="flex items-center gap-2 rounded-lg border border-brand/40 bg-brand/5 px-3 py-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-brand/15 text-brand">
          <Zap className="h-3.5 w-3.5" />
        </span>
        <span className="text-sm">
          <span className="text-muted-foreground">When </span>
          <span className="font-medium">{TRIGGERS.find((t) => t.key === trigger)?.label ?? trigger}</span>
        </span>
      </div>
      {trigger === 'schedule.hourly' && (
        /* Step 120 — the brake. A scheduled rule visits a ticket, acts once,
           and leaves it alone for this long. Without it a note-only sweep
           writes the same note every hour. */
        <div className="flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-xs text-muted-foreground">
          <span>Act on the same ticket at most once every</span>
          <Input
            type="number"
            min={1}
            max={720}
            className="h-7 w-20 text-xs"
            value={String(triggerConfig.cooldownHours ?? 24)}
            onChange={(e) =>
              writeTrigger({ ...triggerConfig, cooldownHours: Math.max(1, Number(e.target.value) || 24) })
            }
            aria-label="Cooldown in hours"
          />
          <span>
            hours. Closed tickets, trashed tickets and anything untouched for six months are never visited.
          </span>
        </div>
      )}

      <StepList
        steps={steps}
        parentId={null}
        depth={0}
        onUpdate={update}
        onRemove={remove}
        onAdd={(parent, type) => append(parent, makeStep(type))}
        valueOptions={valueOptions}
        subflows={subflows}
        collections={collections}
      />
    </div>
  );
}

/**
 * One level of the tree. Recursive, because a loop body is the same thing
 * again — which is also why the add buttons take the parent id: there is never
 * a question of which list a button appends to.
 */
function StepList({
  steps,
  parentId,
  depth,
  onUpdate,
  onRemove,
  onAdd,
  valueOptions,
  subflows,
  collections,
}: {
  steps: Step[];
  parentId: string | null;
  depth: number;
  onUpdate: (id: string, config: Record<string, unknown>) => void;
  onRemove: (id: string) => void;
  onAdd: (parentId: string | null, type: Step['type']) => void;
  valueOptions: (key: string) => { value: string; label: string }[];
  subflows: { id: string; name: string }[];
  collections: { key: string; label: string; needsFieldKey: boolean }[];
}) {
  /**
   * Consecutive actions are drawn as ONE block, not one card each.
   *
   * Kai: *"Is that a good layout to do multiple things"* — it was not. Every
   * action had its own full-width card, its own header and its own trash
   * icon, with a duplicated "+ Condition / + Action" pair under EVERY step.
   * Two actions filled half the screen and it was unclear which button added
   * what.
   *
   * The reference puts several actions INSIDE one node ("Add new action"),
   * which is the right shape: an action list is a list, not a sequence of
   * decisions. Storage is unchanged — each action is still its own graph node
   * walked in order — only the rendering groups them.
   */
  const blocks: { kind: Step['type']; nodes: Step[] }[] = [];
  for (const s of steps) {
    const last = blocks[blocks.length - 1];
    if (s.type === 'action' && last?.kind === 'action') last.nodes.push(s);
    else blocks.push({ kind: s.type, nodes: [s] });
  }

  const HEADS: Record<string, { label: string; icon: typeof Zap; tone: string }> = {
    condition: {
      label: 'Only if',
      icon: GitBranch,
      tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    },
    action: { label: 'Then', icon: Zap, tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' },
    timer: { label: 'Wait', icon: TimerIcon, tone: 'bg-sky-500/15 text-sky-700 dark:text-sky-400' },
    loop: { label: 'For each', icon: Repeat, tone: 'bg-violet-500/15 text-violet-700 dark:text-violet-400' },
    subflow: {
      label: 'Run subflow',
      icon: Workflow,
      tone: 'bg-slate-500/15 text-slate-700 dark:text-slate-300',
    },
  };

  return (
    <div className="space-y-1">
      {blocks.map((block, bi) => {
        const head = HEADS[block.kind] ?? HEADS.action!;
        const Icon = head.icon;
        return (
          <div key={block.nodes[0]!.id}>
            <div className="flex justify-center py-0.5">
              <ArrowDown className="h-3 w-3 text-muted-foreground/40" />
            </div>
            <div className="rounded-lg border bg-card p-3">
              <div className="mb-2 flex items-center gap-2">
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px]',
                    head.tone,
                  )}
                >
                  <Icon className="h-3 w-3" />
                </span>
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {block.kind === 'condition' && bi > 0 ? 'And only if' : head.label}
                </span>
              </div>

              <div className="space-y-2">
                {block.nodes.map((step) => (
                  <div key={step.id}>
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        {step.type === 'condition' ? (
                          /* THE shared builder — Step 42 deleted this file's
                             copy. Three hand-rolled versions of one control
                             is how they drifted, and every drift was a fix
                             applied once and not carried across. */
                          <ConditionBuilder
                            value={step.config.condition as Condition | undefined}
                            onChange={(c) => onUpdate(step.id, { condition: c })}
                            fields={FIELDS}
                            valueOptions={valueOptions}
                            defaultFieldKey="priority"
                          />
                        ) : step.type === 'timer' ? (
                          <TimerRow node={step} onChange={(c) => onUpdate(step.id, c)} />
                        ) : step.type === 'loop' ? (
                          <LoopRow
                            node={step}
                            onChange={(c) => onUpdate(step.id, c)}
                            collections={collections}
                          />
                        ) : step.type === 'subflow' ? (
                          <SubflowRow
                            node={step}
                            onChange={(c) => onUpdate(step.id, c)}
                            subflows={subflows}
                          />
                        ) : (
                          <ActionRow
                            node={step}
                            onChange={(c) => onUpdate(step.id, c)}
                            valueOptions={valueOptions}
                          />
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Remove step"
                        onClick={() => onRemove(step.id)}
                      >
                        <Trash2 />
                      </Button>
                    </div>

                    {/* A loop's body is drawn INSIDE it, indented, so what
                        repeats is visible rather than inferred from order. */}
                    {step.type === 'loop' && (
                      <div className="mt-2 border-l-2 border-violet-500/30 pl-3">
                        {step.body.length === 0 && (
                          <p className="py-1 text-xs text-muted-foreground">
                            Nothing to repeat yet — add a step below.
                          </p>
                        )}
                        <StepList
                          steps={step.body}
                          parentId={step.id}
                          depth={depth + 1}
                          onUpdate={onUpdate}
                          onRemove={onRemove}
                          onAdd={onAdd}
                          valueOptions={valueOptions}
                          subflows={subflows}
                          collections={collections}
                        />
                      </div>
                    )}
                  </div>
                ))}

                {/* "Add another action" lives INSIDE the block it adds to, so
                    there is never a question of which button adds where. */}
                {block.kind === 'action' && (
                  <Button size="sm" variant="ghost" onClick={() => onAdd(parentId, 'action')}>
                    <Plus /> Add another action
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* ONE row of add buttons, at the end of THIS level. */}
      <div className="flex flex-wrap justify-center gap-2 pt-2">
        <Button size="sm" variant="outline" onClick={() => onAdd(parentId, 'condition')}>
          <Plus /> Condition
        </Button>
        <Button size="sm" variant="outline" onClick={() => onAdd(parentId, 'action')}>
          <Plus /> Action
        </Button>
        <Button size="sm" variant="outline" onClick={() => onAdd(parentId, 'timer')}>
          <Plus /> Wait
        </Button>
        {/* No nested loops offered. The engine supports them; a loop inside a
            loop inside a rule is a report, and nobody can read it back. */}
        {depth === 0 && (
          <Button size="sm" variant="outline" onClick={() => onAdd(parentId, 'loop')}>
            <Plus /> For each
          </Button>
        )}
        {subflows.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => onAdd(parentId, 'subflow')}>
            <Plus /> Subflow
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * A TIMER — the node that made all of this necessary.
 *
 * Offered as a number plus a unit rather than raw minutes, because "wait 2880"
 * is a rule nobody re-reads and gets wrong the first time.
 */
function TimerRow({ node, onChange }: { node: Step; onChange: (c: Record<string, unknown>) => void }) {
  const total = Math.max(1, Number(node.config.minutes) || 1);
  const unit = total % 1440 === 0 ? 1440 : total % 60 === 0 ? 60 : 1;
  const amount = Math.round(total / unit);
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Wait</span>
        <Input
          type="number"
          min={1}
          value={String(amount)}
          onChange={(e) =>
            onChange({ ...node.config, minutes: Math.max(1, Number(e.target.value) || 1) * unit })
          }
          className="h-8 w-20"
          aria-label="Wait amount"
        />
        <Select
          value={String(unit)}
          onChange={(e) => onChange({ ...node.config, minutes: amount * Number(e.target.value) })}
          className="h-8 w-28"
          aria-label="Wait unit"
        >
          <option value="1">minutes</option>
          <option value="60">hours</option>
          <option value="1440">days</option>
        </Select>
        <span className="text-sm text-muted-foreground">then carry on</span>
      </div>
      {/* Said on the control, because it is the one thing about a timer that
          surprises people: the steps AFTER it are judged when it wakes. */}
      <p className="text-[11px] text-muted-foreground">
        The ticket is re-read when the wait ends, so anything checked after this point is judged against how
        the ticket is then — not how it was now. Editing or switching off this rule cancels waits already
        running.
      </p>
    </div>
  );
}

/** A LOOP BLOCK. */
function LoopRow({
  node,
  onChange,
  collections,
}: {
  node: Step;
  onChange: (c: Record<string, unknown>) => void;
  collections: { key: string; label: string; needsFieldKey: boolean }[];
}) {
  const key = String(node.config.collection ?? '');
  const def = collections.find((c) => c.key === key);
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">For each of</span>
        <Select
          value={key}
          onChange={(e) => onChange({ ...node.config, collection: e.target.value, fieldKey: '' })}
          className="h-8 w-56"
          aria-label="Collection"
        >
          {collections.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </Select>
        {def?.needsFieldKey && (
          <Input
            value={String(node.config.fieldKey ?? '')}
            onChange={(e) => onChange({ ...node.config, fieldKey: e.target.value })}
            placeholder="field key"
            className="h-8 w-40"
            aria-label="Field key"
          />
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {key === 'linked_tickets'
          ? 'The steps inside act on each linked ticket itself.'
          : 'The steps inside act on this ticket, with {{item}} set to each value.'}{' '}
        At most 50 items.
      </p>
    </div>
  );
}

/** A SUBFLOW call. */
function SubflowRow({
  node,
  onChange,
  subflows,
}: {
  node: Step;
  onChange: (c: Record<string, unknown>) => void;
  subflows: { id: string; name: string }[];
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">Run</span>
        <Select
          value={String(node.config.ruleId ?? '')}
          onChange={(e) =>
            // The NAME is stored alongside the id purely so the Summary
            // sentence reads without a second lookup. The id is the truth.
            onChange({
              ...node.config,
              ruleId: e.target.value,
              ruleName: subflows.find((s) => s.id === e.target.value)?.name ?? '',
            })
          }
          className="h-8 w-64"
          aria-label="Subflow"
        >
          <option value="">Choose a subflow…</option>
          {subflows.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <span className="text-sm text-muted-foreground">then carry on</span>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Only live subflows can be called, and a subflow cannot call itself.
      </p>
    </div>
  );
}

function ActionRow({
  node,
  onChange,
  valueOptions,
}: {
  node: Step;
  onChange: (c: Record<string, unknown>) => void;
  valueOptions: (key: string) => { value: string; label: string }[];
}) {
  const kind = String(node.config.action ?? '');
  /**
   * `assign_to_actor` is presented as a VALUE of "Assign to agent", not as a
   * separate action — it is a choice of who, not a different kind of
   * assignment. Storage keeps the two action keys apart so the engine is
   * untouched; only this mapping is new.
   */
  const isActor = kind === 'assign_to_actor';
  const uiKind = isActor ? 'assign_agent' : kind;
  const def = ACTIONS.find((a) => a.key === uiKind);
  const opts = def?.options ?? valueOptions(uiKind);
  const shownValue = isActor ? ACTOR_VALUE : String(node.config.value ?? '');
  const setValue = (v: string) => {
    if (uiKind === 'assign_agent')
      return onChange(
        v === ACTOR_VALUE ? { action: 'assign_to_actor' } : { action: 'assign_agent', value: v },
      );
    onChange({ ...node.config, value: v });
  };
  /** Some actions take no argument at all. Rendering an empty picker beside
   *  them is what made "Assign to whoever did it" look broken. */
  const takesValue = !def?.free && !def?.novalue;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={uiKind}
          onChange={(e) => onChange({ action: e.target.value, value: '' })}
          className="h-8 w-48"
          aria-label="Action"
        >
          {ACTIONS.map((a) => (
            <option key={a.key} value={a.key}>
              {a.label}
            </option>
          ))}
        </Select>
        {takesValue &&
          (opts.length > 0 ? (
            <Select
              value={shownValue}
              onChange={(e) => setValue(e.target.value)}
              className="h-8 w-52"
              aria-label="Value"
            >
              <option value="">Choose…</option>
              {opts.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              value={shownValue}
              onChange={(e) => setValue(e.target.value)}
              className="h-8 w-44"
              aria-label="Value"
            />
          ))}
      </div>
      {isActor && (
        <p className="text-[11px] text-muted-foreground">
          Only when the ticket is unassigned — so a colleague commenting never takes it off its owner.
        </p>
      )}
      {def?.novalue && (
        <p className="text-[11px] text-muted-foreground">Nothing after this runs for this ticket.</p>
      )}
      {kind === 'add_note' && (
        <Input
          value={String(node.config.value ?? '')}
          onChange={(e) => onChange({ ...node.config, value: e.target.value })}
          placeholder="Note text — {{subject}} and {{priority}} are substituted"
          className="h-8"
          aria-label="Note text"
        />
      )}
      {kind === 'send_email' && (
        <div className="space-y-1.5">
          <Input
            value={String(node.config.to ?? '')}
            onChange={(e) => onChange({ ...node.config, to: e.target.value })}
            placeholder="To — @requester, @assignee, or addresses separated by commas"
            className="h-8"
            aria-label="To"
          />
          <Input
            value={String(node.config.subject ?? '')}
            onChange={(e) => onChange({ ...node.config, subject: e.target.value })}
            placeholder="Subject — {{subject}} is substituted"
            className="h-8"
            aria-label="Subject"
          />
          <Textarea
            value={String(node.config.body ?? '')}
            onChange={(e) => onChange({ ...node.config, body: e.target.value })}
            placeholder="Body — plain text or simple HTML; {{subject}}, {{priority}}, {{status}} are substituted"
            className="min-h-[72px] text-xs"
            aria-label="Body"
          />
          <p className="text-[11px] text-muted-foreground">
            Goes through the desk's outbound mail and is logged there. Nothing is sent to an address that is
            not on the ticket or written here.
          </p>
        </div>
      )}
      {kind === 'web_request' && (
        <div className="space-y-1.5">
          {/* METHOD and HEADERS: the engine has always read both — a webhook
              that needs an auth header or a PUT could be executed but not
              configured, which is the same class of hole as the missing
              actions. */}
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={String(node.config.method ?? 'POST')}
              onChange={(e) => onChange({ ...node.config, method: e.target.value })}
              className="h-8 w-28"
              aria-label="Method"
            >
              {['POST', 'GET', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
            <span className="text-xs text-muted-foreground">to</span>
          </div>
          <Input
            value={String(node.config.url ?? '')}
            onChange={(e) => onChange({ ...node.config, url: e.target.value })}
            placeholder="https://hooks.example.com/…"
            className="h-8"
            aria-label="URL"
          />
          <Input
            value={String(node.config.body ?? '')}
            onChange={(e) => onChange({ ...node.config, body: e.target.value })}
            placeholder='{"text":"{{subject}} is now {{priority}}"}'
            className="h-8 font-mono text-xs"
            aria-label="Body"
          />
          <Input
            value={headerText(node.config.headers)}
            onChange={(e) => onChange({ ...node.config, headers: parseHeaders(e.target.value) })}
            placeholder="Headers — Authorization: Bearer …, one per line or comma-separated"
            className="h-8 font-mono text-xs"
            aria-label="Headers"
          />
          {/* Said plainly on the control, not buried in docs: an admin
              configuring a webhook should know the boundary before they hit a
              refusal they cannot explain. */}
          <p className="text-[11px] text-muted-foreground">
            Public addresses only — private, loopback and cloud-metadata ranges are refused, redirects are
            re-checked, and credentials are never forwarded across origins.
          </p>
        </div>
      )}
    </div>
  );
}

/** Headers are stored as an object and edited as text, because a key/value
 *  grid for what is usually one Authorization line is more UI than the job
 *  needs. Round-trips exactly. */
function headerText(v: unknown): string {
  if (!v || typeof v !== 'object') return '';
  return Object.entries(v as Record<string, string>)
    .map(([k, val]) => `${k}: ${val}`)
    .join(', ');
}

function parseHeaders(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of text.split(/[\n,]/)) {
    const i = part.indexOf(':');
    if (i <= 0) continue;
    const k = part.slice(0, i).trim();
    const v = part.slice(i + 1).trim();
    if (k) out[k] = v;
  }
  return out;
}
